// src/api/tickets.js
import axios from "axios";
import { API_BASE as SBIN_API_BASE } from "./base";

const API = SBIN_API_BASE;

function optionalContextHeaders() {
  const headers = {};
  const salonId = localStorage.getItem("salon_id");
  const businessId = localStorage.getItem("business_id");

  if (salonId !== null && salonId !== undefined && String(salonId).trim() !== "") {
    headers["X-Salon-Id"] = String(salonId).trim();
  }
  if (businessId !== null && businessId !== undefined && String(businessId).trim() !== "") {
    headers["X-Business-Id"] = String(businessId).trim();
  }
  return headers;
}

function authHeaders() {
  const token = null;
  const contextHeaders = optionalContextHeaders();
  return token
    ? {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...contextHeaders,
      }
    : { "Content-Type": "application/json", ...contextHeaders };
}

function parseFilenameFromContentDisposition(contentDisposition, fallbackName) {
  const headerValue = String(contentDisposition || "");
  const utf8Match = headerValue.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/["']/g, "").trim());
    } catch (_) {
      // Fallback to non-encoded filename parsing below.
    }
  }
  const basicMatch = headerValue.match(/filename="?([^";]+)"?/i);
  if (basicMatch?.[1]) {
    return basicMatch[1].trim();
  }
  return fallbackName;
}

export async function getTickets(params = {}) {
  const res = await axios.get(`${API}/tickets`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function createTicket(data) {
  const res = await axios.post(`${API}/tickets`, data, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getTicketById(id) {
  const res = await axios.get(`${API}/tickets/${id}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function updateTicket(id, data) {
  const res = await axios.put(`${API}/tickets/${id}`, data, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function addTicketItems(id, items) {
  const res = await axios.post(`${API}/tickets/${id}/items`, items, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function addTicketServices(id, payload) {
  const res = await axios.post(`${API}/tickets/${id}/services`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getTicketServiceStaffAvailability(id, payload) {
  const res = await axios.post(
    `${API}/tickets/${id}/service-staff-availability`,
    payload,
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function addTicketProducts(id, payload) {
  const res = await axios.post(`${API}/tickets/${id}/products`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function addTicketGiftCard(id, payload) {
  const res = await axios.post(`${API}/tickets/${id}/gift-cards`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function deleteTicketItem(id, itemId) {
  const res = await axios.delete(`${API}/tickets/${id}/items/${itemId}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function deleteTicket(ticketId) {
  const res = await axios.delete(`${API}/tickets/${ticketId}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function addTicketPayment(id, data) {
  const res = await axios.post(`${API}/tickets/${id}/payments`, data, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function listTicketPayments(id) {
  const res = await axios.get(`${API}/tickets/${id}/payments`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function saveTicketPayments(id, payload) {
  const res = await axios.post(`${API}/tickets/${id}/payments/batch`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function deleteTicketPayment(ticketId, paymentId) {
  const res = await axios.delete(
    `${API}/tickets/${ticketId}/payments/${paymentId}`,
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function closeTicket(ticketId) {
  const res = await axios.post(`${API}/tickets/${ticketId}/close`, null, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function updateTicketItem(ticketId, itemId, data) {
  const res = await axios.patch(
    `${API}/tickets/${ticketId}/items/${itemId}`,
    data,
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function setTicketTaxExempt(ticketId, isTaxExempt) {
  const res = await axios.patch(
    `${API}/tickets/${ticketId}/tax-exempt`,
    { is_tax_exempt: isTaxExempt },
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function setTicketTips(ticketId, payload) {
  const res = await axios.put(`${API}/tickets/${ticketId}/tips`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function reopenTicket(ticketId) {
  const res = await axios.post(`${API}/tickets/${ticketId}/reopen`, null, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function createPaymentInTicket(payload) {
  const res = await axios.post(`${API}/tickets/payment-in`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function createPaymentOutTicket(payload) {
  const res = await axios.post(`${API}/tickets/payment-out`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function createHouseSaleTicket(params = {}) {
  const res = await axios.post(`${API}/tickets/house-sale`, params, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getRefundPreview(ticketId) {
  const res = await axios.get(`${API}/tickets/${ticketId}/refunds/preview`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getRefundContext(ticketId) {
  const res = await axios.get(`${API}/tickets/${ticketId}/refunds/context`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function listTicketRefunds(ticketId) {
  const res = await axios.get(`${API}/tickets/${ticketId}/refunds`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function createTicketRefund(ticketId, payload) {
  const res = await axios.post(`${API}/tickets/${ticketId}/refunds`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function updateTicketRefund(ticketId, payload) {
  const res = await axios.put(`${API}/tickets/${ticketId}/refunds`, payload, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function downloadTicketReceiptPdf(ticketId) {
  const res = await axios.get(`${API}/tickets/${ticketId}/receipt/pdf`, {
    headers: authHeaders(),
    responseType: "blob",
  });
  const filename = parseFilenameFromContentDisposition(
    res.headers?.["content-disposition"],
    `ticket-${ticketId}-receipt.pdf`
  );
  return { blob: res.data, filename };
}

export async function emailTicketReceipt(ticketId) {
  const res = await axios.post(`${API}/tickets/${ticketId}/receipt/email`, null, {
    headers: authHeaders(),
  });
  return res.data;
}
